import { toast } from "sonner";
import config from "../config";
import {
  getToken,
  setToken,
  getRefreshToken,
  setRefreshToken,
  getAccessExpiry,
  setAccessExpiry
} from "./storageUtils";

/**
 * En-têtes envoyés sur **toutes** les requêtes, refresh compris.
 *
 * <p>{@code Accept-Language: fr} est indispensable : le back résout ses
 * messages i18n depuis cet en-tête. Sans lui, un navigateur configuré en
 * anglais reçoit les messages d'erreur en anglais (« An unexpected error
 * occurred », « Bad credentials »…), qui remontent tels quels dans les
 * toasts.
 */
const DEFAULT_HEADERS = {
  "Content-Type": "application/json",
  Accept: "application/json",
  "Accept-Language": "fr"
};

/**
 * Message de repli quand le back ne fournit aucun message exploitable
 * (body non JSON, 502 du proxy, erreur hors {@code GlobalExceptionHandler}).
 * On n'affiche jamais {@code response.statusText} : c'est la reason phrase
 * HTTP, toujours en anglais (« Internal Server Error »…).
 */
const FALLBACK_ERROR_MESSAGE =
  "Une erreur inattendue est survenue. Veuillez réessayer.";

/** Message de repli quand le réseau/serveur est injoignable. */
const NETWORK_ERROR_MESSAGE =
  "Connexion au serveur impossible. Veuillez vérifier votre connexion internet.";

/**
 * Toast global d'erreur — appelé sur chaque retour `!ok` du wrapper
 * `apiRequest`. Centralise la notification utilisateur pour ne plus
 * avoir à écrire `toast.error(result.message)` dans chaque
 * onSuccess/saga.
 *
 * <p>Exceptions :
 * <ul>
 *   <li>{@code status === 401} : déjà géré par le flux refresh / logout
 *       — l'utilisateur est redirigé, inutile de bruiter un toast.</li>
 *   <li>{@code options.silent === true} : opt-out explicite pour les
 *       appelants qui veulent gérer l'erreur eux-mêmes (ex.
 *       FinancialTransactionModal qui affiche une Alert inline).</li>
 * </ul>
 *
 * <p>Sonner dédup les toasts qui partagent le même {@code id} — on
 * utilise le message comme id pour éviter les rafales identiques (3
 * retries react-query par défaut, ou plusieurs widgets qui chargent le
 * même endpoint en parallèle).
 */
const notifyApiError = (result, { silent = false } = {}) => {
  if (silent) return;
  if (result?.status === 401) return;
  const msg =
    result?.message || result?.error || FALLBACK_ERROR_MESSAGE;
  toast.error(msg, { id: msg });
};

/**
 * Wrapper fetch unifié.
 *
 * <p>Aligne le shape de réponse front sur le contrat back actuel :
 * <ul>
 *   <li>Succès 2xx → {@code { ok: true, status, data }}
 *       où {@code data} est le payload JSON brut renvoyé par le contrôleur
 *       (DTO direct, ID, liste, etc.). <strong>Plus de wrap
 *       {success, message, data}</strong>.</li>
 *   <li>Erreur 4xx/5xx → {@code { ok: false, status, message, error, raw }}
 *       en parsant le shape uniforme de {@code GlobalExceptionHandler} :
 *       {@code { status, error, message, timestamp }}.</li>
 *   <li>Échec réseau → {@code { ok: false, status: 0, message }}.</li>
 * </ul>
 *
 * <p>Ajoute automatiquement {@code Authorization: Bearer <token>} si un token
 * est présent dans le localStorage (clé "Bearer"). Lecture per-request, pas
 * au chargement du module — important pour ne pas perdre le token après login.
 *
 * <h3>Refresh JWT (Phase 2.B+ — P1-4 + P2-8)</h3>
 * <ol>
 *   <li><strong>Refresh préemptif</strong> : si {@code AccessExpiry} expire
 *       dans moins de {@link #REFRESH_THRESHOLD_MS}, on demande un nouveau
 *       couple access/refresh <em>avant</em> d'envoyer la requête.</li>
 *   <li><strong>Réaction au 401</strong> : si le serveur répond 401, on tente
 *       un refresh puis on rejoue <em>une seule fois</em> la requête initiale
 *       avec le nouveau token.</li>
 *   <li><strong>Single-flight</strong> : un seul appel
 *       {@code POST /users/refresh-token} concurrent — toutes les requêtes
 *       qui déclenchent un refresh attendent la même promesse, évitant un
 *       feu d'artifice de refresh sur l'ouverture d'une page qui charge 10
 *       endpoints en parallèle.</li>
 *   <li><strong>Échec du refresh</strong> : on purge la session via
 *       {@link logoutHelper} et on retourne {@code {ok:false, status:401}}
 *       à l'appelant — qui n'a pas à gérer le cas spécifiquement.</li>
 * </ol>
 *
 * <p>Les routes {@code /users/sign-in} et {@code /users/refresh-token} sont
 * exclues de la logique de refresh : la première parce qu'elle est publique,
 * la seconde parce qu'elle <em>est</em> le refresh (sinon récursion infinie).
 *
 * @param {string} path  Chemin relatif (ex. {@code "/users/me"}). Sera
 *                       préfixé par {@code config.API}.
 * @param {{
 *   method?: string,
 *   body?: any,
 *   headers?: Record<string, string>,
 *   auth?: boolean,
 *   signal?: AbortSignal,
 *   _retry?: boolean
 * }} [options]
 *   - {@code method}  : verbe HTTP, défaut "GET"
 *   - {@code body}    : objet sérialisé en JSON, ou string brute, ou undefined
 *   - {@code headers} : surcharge d'en-têtes
 *   - {@code auth}    : forcer/desactiver l'envoi du Bearer (défaut : true)
 *   - {@code signal}  : AbortSignal pour annuler la requête
 *   - {@code _retry}  : interne — flag posé pendant le rejeu post-refresh
 *
 * @returns {Promise<{ ok: boolean, status: number, data?: any, message?: string, error?: string, raw?: any }>}
 */

/** Seuil de refresh préemptif : 60 s avant expiration de l'access token. */
const REFRESH_THRESHOLD_MS = 60_000;

/** Routes qui ne doivent jamais déclencher la mécanique de refresh. */
const REFRESH_BLACKLIST = ["/users/sign-in", "/users/refresh-token"];

/**
 * Promesse courante du refresh en vol — sert de mutex single-flight pour
 * ne lancer qu'un seul {@code POST /users/refresh-token} simultané.
 * @type {Promise<boolean> | null}
 */
let refreshInFlight = null;

/**
 * Tente un refresh côté back. Renvoie {@code true} si un nouvel access
 * token est en localStorage à la fin, {@code false} sinon (le refresh
 * a échoué — l'appelant doit déclencher un logout).
 *
 * <p>L'import dynamique de {@code logoutHelper} évite une dépendance
 * cyclique avec {@code utils/logoutHelper.js} (qui importe storageUtils
 * mais pas apiRequest).
 */
async function performRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  let response;
  try {
    response = await fetch(config.API + "/users/refresh-token", {
      method: "POST",
      headers: { ...DEFAULT_HEADERS },
      body: JSON.stringify({ refreshToken }),
      redirect: "follow"
    });
  } catch (_networkError) {
    return false;
  }

  if (!response.ok) return false;

  let data;
  try {
    data = await response.json();
  } catch (_) {
    return false;
  }

  const { accessToken, refreshToken: nextRefresh, accessExpiry } = data || {};
  if (!accessToken) return false;

  setToken(accessToken);
  if (nextRefresh) setRefreshToken(nextRefresh);
  if (accessExpiry) setAccessExpiry(accessExpiry);
  return true;
}

/**
 * Garantit qu'un seul refresh est en vol à tout instant. Toutes les
 * requêtes qui en réclament un partagent la même promesse.
 */
function refreshOnce() {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Vrai si l'access token expire dans moins de {@link REFRESH_THRESHOLD_MS}.
 * Renvoie {@code false} si {@code AccessExpiry} est absent ou illisible
 * (le serveur tranchera via 401 — pas de refresh préemptif aveugle).
 */
function shouldRefreshPreemptively() {
  const expiry = getAccessExpiry();
  if (!expiry) return false;
  const expiryMs = Date.parse(expiry);
  if (Number.isNaN(expiryMs)) return false;
  return expiryMs - Date.now() < REFRESH_THRESHOLD_MS;
}

async function logoutAndForget() {
  // Import dynamique pour éviter une boucle d'import au chargement du module.
  const { logoutHelper } = await import("./logoutHelper");
  logoutHelper();
}

export async function apiRequest(path, options = {}) {
  const {
    method = "GET",
    body,
    headers = {},
    auth = true,
    signal,
    responseType = "json",
    _retry = false
  } = options;

  const isRefreshable = auth && !REFRESH_BLACKLIST.includes(path);

  // Refresh préemptif — uniquement si on est censé être authentifié.
  if (isRefreshable && !_retry && getToken() && shouldRefreshPreemptively()) {
    await refreshOnce();
  }

  const finalHeaders = {
    ...DEFAULT_HEADERS,
    ...headers
  };

  if (auth) {
    const token = getToken();
    if (token) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
  }

  let payload;
  if (body !== undefined && body !== null) {
    payload = typeof body === "string" ? body : JSON.stringify(body);
  }

  let response;
  try {
    response = await fetch(config.API + path, {
      method,
      headers: finalHeaders,
      body: payload,
      signal,
      redirect: "follow"
    });
  } catch (networkError) {
    // Une annulation volontaire (AbortSignal, ex. démontage du composant)
    // n'est pas une panne réseau : on ne notifie pas l'utilisateur.
    const isAborted = networkError?.name === "AbortError";
    // `networkError.message` est produit par le navigateur, donc en anglais
    // (« Failed to fetch ») — on ne l'affiche jamais tel quel.
    const message = isAborted ? "Requête annulée" : NETWORK_ERROR_MESSAGE;
    const error = isAborted ? "AbortError" : "NetworkError";
    // Side-effect AVANT le return inline : éviter une variable temporaire
    // qui élargirait le littéral `ok: false` en `ok: boolean` côté TS et
    // casserait la discrimination du union chez les consommateurs.
    if (!isAborted) {
      notifyApiError({ status: 0, message, error }, options);
    }
    return { ok: false, status: 0, message, error };
  }

  // 401 + token présent + refresh possible → tente un refresh + un rejeu.
  if (
    response.status === 401 &&
    isRefreshable &&
    !_retry &&
    getToken()
  ) {
    const refreshed = await refreshOnce();
    if (refreshed) {
      return apiRequest(path, { ...options, _retry: true });
    }
    // Refresh échoué → session morte.
    await logoutAndForget();
    return {
      ok: false,
      status: 401,
      message: "Session expirée",
      error: "Unauthorized"
    };
  }

  // 204 No Content : pas de body à parser.
  if (response.status === 204) {
    return { ok: true, status: 204, data: null };
  }

  // Mode blob (téléchargements binaires : PDF, etc.). On lit le body en blob
  // sur succès, et on retombe sur le parsing JSON/text pour extraire le
  // shape `GlobalExceptionHandler` quand le back renvoie une erreur.
  if (responseType === "blob" && response.ok) {
    try {
      const data = await response.blob();
      return { ok: true, status: response.status, data };
    } catch (_blobError) {
      // Message figé : `blobError.message` vient du navigateur, en anglais.
      const message = "Lecture du fichier impossible.";
      notifyApiError(
        { status: response.status, message, error: "BlobReadError" },
        options
      );
      return {
        ok: false,
        status: response.status,
        message,
        error: "BlobReadError"
      };
    }
  }

  const contentType = response.headers.get("content-type") || "";
  let data = null;

  if (contentType.includes("application/json")) {
    try {
      data = await response.json();
    } catch (_parseError) {
      // Body annoncé JSON mais corrompu — on continue avec data=null.
      data = null;
    }
  } else {
    // Body texte (rare : webhook, certaines erreurs). On le garde brut.
    try {
      data = await response.text();
    } catch (_) {
      data = null;
    }
  }

  if (response.ok) {
    return { ok: true, status: response.status, data };
  }

  // Erreur — tente de parser le shape GlobalExceptionHandler.
  const isErrorEnvelope =
    data && typeof data === "object" && (data.message || data.error);

  const message =
    (isErrorEnvelope && data.message) || FALLBACK_ERROR_MESSAGE;
  // `error` reste technique (jamais affiché seul) : on garde la reason
  // phrase HTTP quand le back n'a pas renvoyé son enveloppe d'erreur.
  const error = isErrorEnvelope ? data.error : response.statusText;
  notifyApiError(
    { status: response.status, message, error },
    options
  );
  return {
    ok: false,
    status: response.status,
    message,
    error,
    raw: data
  };
}

/** Sucre syntaxique pour les verbes courants. */
apiRequest.get = (path, options) => apiRequest(path, { ...options, method: "GET" });
apiRequest.post = (path, body, options) =>
  apiRequest(path, { ...options, method: "POST", body });
apiRequest.put = (path, body, options) =>
  apiRequest(path, { ...options, method: "PUT", body });
apiRequest.del = (path, options) => apiRequest(path, { ...options, method: "DELETE" });

export default apiRequest;
